/**
 * The printable cheat sheet (closing/k3) and the 5-day challenge (closing/k2), in the
 * three steps of the case page: read the mail, check the draft, do the one action.
 * Round 3 (12 Oct 2026): Simplified Technical English; the six icon lines are drawn
 * with their icons by CheatSheetView (CHEAT_LEGEND_ICONS of the status legend figure).
 */

import type { Challenge, CheatSheet } from "@/features/training/types";

export const CHEAT_SHEET: CheatSheet = {
  title: "I2Q cheat sheet for sales",
  rows: [
    {
      job: "See all open work",
      where:
        "Sidebar > Queue > chips `open, all dates`, `open older than 7 days`; tab `Ready to quote`",
      check:
        "The default view shows only today. The Action column says what each case needs. Start with `Ready to quote`: you can finish these cases at once.",
    },
    {
      job: "Read the mail",
      where: "Case > the mail card at the top, the customer line in it",
      check:
        "`Confirmed` means I2Q is sure. `Confirm` is one click. `Find customer` opens the search.",
    },
    {
      job: "Confirm the customer",
      where: "Case > customer line > `Why` > `Confirm` ({key:c})",
      check: "Confirm only with evidence. Otherwise `Not them`, then `Find customer`.",
    },
    {
      job: "Check the draft",
      where:
        "Case > the table in the middle, `Needs you` on top, then `Matched`, then `Nothing to quote`",
      check:
        "An icon and a word per row. `matched`: nothing to do. `confirm`: one click. `pick`: pick one from the list. `no item`: tell the customer.",
    },
    {
      job: "Correct a row",
      where:
        "Case > row > `Confirm` or `Pick item` at the right end; `Qty` cell: click, type, Enter; strip > `Set quantity for all lines`; chevron on the status > `Correct`, `No item`, `Undo`; the three dots > `Change item`, `Correct quantity`, `Set price`",
      check:
        "Compare the quantity, the unit and the item with the customer's own words. Click the customer's words and the mail card marks them. Keys in the draft: Enter confirms, {key:n} no item, {key:p} price, {key:q} quantity, {key:i} pick item.",
    },
    {
      job: "Read a price",
      where:
        "Case > `Price` column, the band icon; chevron on the status for the list price and the history",
      check:
        "Compare with the last quotation. `Check: Price approval needed`: your manager clicks `Approve price` on the big button, in the row menu or under `More`.",
    },
    {
      job: "Do the one action",
      where: "Case > the big button in the bar at the bottom ({key:a} accept, {key:r} copy)",
      check:
        "The button names the next step and changes as you work. When a case does not go through, click the sentence next to the button, or open `Open points`. `Nothing to do`: press it and go to the next case.",
    },
    {
      job: "Reply to the customer",
      where: "Case > `Copy the reply` ({key:r}); `More` > `Edit text`",
      check:
        "No attachment sentence. Right quantities. A lead time or an open point for each line. Send from Outlook.",
    },
    {
      job: "Set the status",
      where: "Case > `More` > `Set status`",
      check: "Closed is not a reply. Check the thread. Then `Reopen` if the customer still waits.",
    },
    {
      job: "Everything else",
      where: "Case > `Details`; `More` > `Give feedback`; Sidebar > `Rules and countries`; {key:?}",
      check:
        "Details holds the checks, the quotation and the history. Feedback tells the build team.",
    },
  ],
  // Lines 1 to 4, 7 and 8 of the legend (lib/line-icons.ts LEGEND), compressed to six.
  icons: {
    title: "What the icons mean",
    lines: [
      "Green check: matched, nothing to do.",
      "Blue question mark: confirm, press `Confirm` in the row.",
      "Orange box with a magnifier: pick, press `Pick item` in the row.",
      "Grey ban sign: no item, tell the customer.",
      "On a price: check badge in band, warning triangle below margin, dashed circle no price. Person with a check on a price: set by a person (row menu: Set price, Undo price).",
      "On a lead time: clock in F&O, clock with an arrow from an earlier quotation, dashed circle none in F&O, hourglass not looked up yet.",
    ],
  },
};

export const CHALLENGE: Challenge = {
  title: "Your 5-day challenge",
  items: [
    { id: "ch-take", text: "Open every case you work on and press its one action." },
    { id: "ch-older", text: "Open the chip `open older than 7 days` once a day." },
    {
      id: "ch-evidence",
      text: "Confirm a customer only after you read the evidence under `Why`.",
    },
    { id: "ch-reply", text: "Check one reply line by line before you send it." },
    {
      id: "ch-reopen",
      text: "Reopen one closed case that still needs a reply, or use `Pick item` once.",
    },
  ],
};
